import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { RequestFileDto } from 'src/app/procedures/models';
import { IdentityService } from 'src/app/@core/services/identity.service';
import { createFieldId, describedBy } from '../text-input/field-id';
import {
  acceptLabel,
  FileUploadStatus,
  FileValidationError,
  formatFileSize,
  validateFile,
} from './file-validation';

@Component({
  selector: 'ngx-file-input',
  templateUrl: './file-input.component.html',
  styleUrls: ['./file-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileInputComponent),
      multi: true,
    },
  ],
})
export class FileInputComponent implements OnInit, ControlValueAccessor {
  @Input() id: any;
  @Input() label = '';
  @Input() accept = '*';
  @Input() require = false;
  @Input() fireAlert = false;
  @Input() readonly = false;
  @Input() showProgress: boolean = false;
  @Input() progress: number = 0;
  /** Unified Code mode: largest accepted file in MB (null/0 = no limit). Enforced only in Unified mode. */
  @Input() maxSizeMb: number = 10;
  /** Unified Code mode: set by the parent when the upload request failed. */
  @Input() uploadFailed = false;
  _fileName: string;
  /// Used only when pass existing file name from parent component
  @Input() set fileName(val: string) {
    this._fileName = val ? val : null;
    this.type = val ? val.split(".").splice(1).toString() : null;
    this.progress = val ? 100 : 0;
  }
  get fileName() {
    return this._fileName;
  }
  @Output() onChange = new EventEmitter<any>();
  @Output() onDownloadClick = new EventEmitter();
  @Output() onDeleteClick = new EventEmitter();

  type: string;
  size: string;

  // ---- Unified Code state
  isDragOver = false;
  touched = false;
  validationError: FileValidationError | null = null;
  private readonly autoId = createFieldId('uc-file');

  constructor(private identityService: IdentityService) { }

  get isUnified(): boolean {
    return this.identityService.isUnifiedCode();
  }

  get inputDomId(): string {
    return 'fileInput' + (this.id ?? this.autoId);
  }

  get helperId(): string {
    return `${this.inputDomId}-helper`;
  }

  get errorId(): string {
    return `${this.inputDomId}-error`;
  }

  get allowedTypes(): string {
    return acceptLabel(this.accept);
  }

  get status(): FileUploadStatus {
    if (this.uploadFailed) return 'failed';
    if (this.showProgress && this.progress > 0 && this.progress < 100) return 'uploading';
    if (this.progress === 100) return 'completed';
    return 'pending';
  }

  get showRequiredError(): boolean {
    return this.require && !this.fileName && (this.fireAlert || this.touched);
  }

  get hasUnifiedError(): boolean {
    return !!this.validationError || (this.uploadFailed && !!this.fileName) || this.showRequiredError;
  }

  get unifiedDescribedBy(): string | null {
    return describedBy(this.helperId, this.hasUnifiedError && this.errorId);
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    if (!this.readonly) this.isDragOver = true;
  }

  onDragLeave() {
    this.isDragOver = false;
  }

  onUnifiedDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
    if (this.readonly) return;
    this.selectFile(event.dataTransfer?.files?.[0]);
  }

  onUnifiedSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    this.selectFile(input.files?.[0]);
    // Reset so selecting the same (rejected) file again still fires a change event.
    input.value = '';
  }

  private selectFile(file: File | undefined) {
    this.touched = true;
    if (!file) return;
    this.validationError = validateFile(file, this.accept, this.maxSizeMb);
    if (this.validationError) return;

    this.inputValue = file;
    this.onChange.emit(this.inputValue);
    this.prepareFileData();
    this.size = formatFileSize(file.size);
  }

  _inputValue = undefined;
  get inputValue() {
    return this._inputValue;
  }
  set inputValue(val) {
    this._inputValue = val;
    this.propagateChange(this._inputValue);
  }

  propagateChange = (_: any) => { };
  registerOnChange(fn: any) {
    this.propagateChange = fn;
  }
  registerOnTouched() { }
  writeValue(value: any) {
    if (value !== undefined) {
      this.inputValue = value;
    }
  }
  ngOnInit() { }
  onDownload() {
    this.onDownloadClick.emit();
  }
  onDelete() {
    this.touched = true;
    this.validationError = null;
    this.inputValue = undefined;
    this.progress = 0;
    this.onDeleteClick.emit();
  }
  dragOver(event: any) { event.preventDefault() }
  dropFile(event: any) {
    event.preventDefault()

    this.inputValue = event.type === "drop"
      ? event.dataTransfer.files[0] : event.target.files[0];

    this.onChange.emit(this.inputValue);
    this.prepareFileData();
  }
  prepareFileData() {
    this.fileName = this.inputValue.name;
    this.progress = 0;

    if (this.inputValue.size <= 1000000) {
      this.size = (this.inputValue.size / 1000).toFixed(2) + "kb"
    } else if (this.inputValue.size == 1000000 || this.inputValue.size <= 1000000000) {
      this.size = (this.inputValue.size / 1000000).toFixed(2) + "mb";
    } else if (this.inputValue.size == 1000000000 || this.inputValue.size <= 1000000000000) {
      this.size = (this.inputValue.size / 1000000000).toFixed(2) + "gb";
    }
  }
}
